import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, fetchMe, ApiError } from '../api/client';
import { useAuthStore } from '../auth/store';
import { toast } from '../components/Toasts';
import { Button, FormField, Input } from '../components/ui';

// Forced password change. AppShell routes the user here whenever
// me.user.must_change_password is set (admin-created accounts and admin
// password resets land here on next login). On success we refetch /me — which
// clears the flag server-side — re-hydrate the session, and return to the app.
export function ForcePasswordChangePage(): JSX.Element {
  const setMe = useAuthStore((s) => s.setMe);
  const navigate = useNavigate();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (next !== confirm) {
      toast.warn('New password and confirmation do not match.');
      return;
    }
    setBusy(true);
    try {
      await api.post('/api/auth/change-password', {
        current_password: current,
        new_password: next,
      });
      const { user, ...rest } = await fetchMe();
      setMe(user ? { user, ...rest } : null);
      toast.success('Password updated.');
      navigate('/app', { replace: true });
    } catch (err) {
      toast.danger(
        err instanceof ApiError ? err.message || 'Password change failed.' : 'Password change failed.',
      );
      setBusy(false);
    }
  };

  return (
    <div className="page">
      <section className="settings-card force-password-card">
        <h2>Choose a new password</h2>
        <p className="settings-hint">
          Your account requires a new password before you can continue. Minimum 12
          characters.
        </p>
        <form onSubmit={onSubmit} className="settings-form">
          <FormField label="Current (temporary) password" required>
            <Input
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              required
            />
          </FormField>
          <FormField label="New password" required>
            <Input
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              minLength={12}
              required
            />
          </FormField>
          <FormField label="Confirm new password" required>
            <Input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              minLength={12}
              required
            />
          </FormField>
          <div className="settings-actions">
            <Button type="submit" variant="primary" disabled={busy}>
              {busy ? 'Saving…' : 'Set new password'}
            </Button>
          </div>
        </form>
      </section>
    </div>
  );
}
